import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { isNativeApp } from '@/utils/platform';
import alephLogo from '../assets/logos/Aleph-Logomark-Blue.png';
import alephLogoWhite from '../assets/logos/Aleph-Logomark-White.png';
import MoonIcon from './icons/MoonIcon';
import SunIcon from './icons/SunIcon';

interface FooterProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToSignup?: (source?: string) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToPricing?: () => void;
  /**
   * Reopens the cookie consent banner so a visitor can change their mind
   * (GDPR gives them the right to withdraw consent as easily as they gave it).
   * The banner is mounted app-wide, so every footer can offer this.
   */
  onOpenCookiePreferences?: () => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigateToExplore, onNavigateToLibrary, onNavigateToSignup, onNavigateToTerms, onNavigateToPrivacy, onNavigateToAbout, onNavigateToPricing, onOpenCookiePreferences }) => {
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <footer
      role="contentinfo"
      className={`hidden lg:block border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
      style={{
        backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)',
      }}
    >
      <div className="w-full px-6 sm:px-8 lg:px-16 xl:px-24 py-4">
        <div className="flex flex-col lg:flex-row justify-between items-center max-w-6xl mx-auto">
          {/* Left side - Logo */}
          <div className="flex items-center mb-4 lg:mb-0">
            <img
              src={isDarkMode ? alephLogoWhite : alephLogo}
              alt="Aleph Logo"
              className="w-8 h-8 object-contain mr-2"
            />
            <span className={`text-lg font-bold ${
              isDarkMode ? 'text-white' : 'text-blue-700'
            }`}>Aleph</span>
          </div>

          {/* Right side - Navigation and Theme Toggle */}
          <div className="flex items-center space-x-6">
            <nav role="navigation" aria-label="Footer navigation" className="flex items-center space-x-4 lg:space-x-5 xl:space-x-6">
              <button
                onClick={onNavigateToExplore}
                aria-label="Navigate to explore page"
                className={`cursor-pointer text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                }`}
              >
                Explore
              </button>
              <button
                onClick={onNavigateToLibrary}
                aria-label="Navigate to library page"
                className={`cursor-pointer text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                }`}
              >
                Library
              </button>
              {!isNativeApp() && (
                <button
                  onClick={onNavigateToPricing}
                  aria-label="Navigate to pricing page"
                  className={`cursor-pointer text-sm hover:text-blue-600 transition-colors ${
                    isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                  }`}
                >
                  Pricing
                </button>
              )}
              <button
                onClick={onNavigateToAbout}
                aria-label="Navigate to about page"
                className={`cursor-pointer text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                }`}
              >
                About
              </button>
              {/* <button
                onClick={onNavigateToSignup}
                aria-label="Join Aleph by signing up"
                className={`text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-black-700 hover:text-blue-600'
                }`}
              >
                Join
              </button> */}
              <button
                onClick={onNavigateToTerms}
                aria-label="Navigate to terms and conditions page"
                className={`cursor-pointer text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                }`}
              >
                Terms
              </button>
              <button
                onClick={onNavigateToPrivacy}
                aria-label="Navigate to privacy notice page"
                className={`cursor-pointer text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                }`}
              >
                Privacy
              </button>
              {onOpenCookiePreferences && (
                <button
                  onClick={onOpenCookiePreferences}
                  aria-label="Change your cookie and analytics choices"
                  className={`cursor-pointer text-sm hover:text-blue-600 transition-colors ${
                    isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                  }`}
                >
                  Cookies
                </button>
              )}
            </nav>

            {/* Social Links */}
            <div className="flex items-center space-x-4 lg:space-x-5 xl:space-x-6 border-l border-gray-300 dark:border-gray-600 pl-4 lg:pl-5 xl:pl-6">
              <a
                href="https://substack.com/@hialeph"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit our Substack publication"
                className={`text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                }`}
              >
                Substack
              </a>
              <a
                href="https://www.instagram.com/alephapp/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
                className={`text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                }`}
              >
                Instagram
              </a>
              <a
                href="https://www.linkedin.com/company/hi-aleph/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect with us on LinkedIn"
                className={`text-sm hover:text-blue-600 transition-colors ${
                  isDarkMode ? 'text-black-300 hover:text-blue-400' : 'text-theme-primary-light hover:text-blue-600'
                }`}
              >
                LinkedIn
              </a>
            </div>

            {/* Theme Toggle */}
            <div className="flex items-center border-l border-gray-300 dark:border-gray-600 pl-4 lg:pl-5 xl:pl-6">
              <button
                onClick={toggleTheme}
                aria-label={isDarkMode ? "Switch to light theme" : "Switch to dark theme"}
                className={`py-0.5 rounded-full transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'hover:bg-black-700'
                    : 'hover:bg-black-100'
                }`}
              >
                {isDarkMode ? (
                  <SunIcon className="w-4 h-4 text-theme-primary-dark" />
                ) : (
                  <MoonIcon className="w-4 h-4 text-theme-primary-light" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
